import React, { useState } from "react";
import { Input, Switch, Button, Typography } from "antd";
import { SendOutlined, DeleteOutlined } from "@ant-design/icons";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import GradientButton from "../UI/GradientButton";
import "./index.scss";

const { Title } = Typography;

interface PollCreatorProps {
  onSend: (question: string, options: any[], allowMultiple: boolean) => void;
}

const MIN_OPTIONS = 2;
const MAX_OPTIONS = 12;

const PollCreator: React.FC<PollCreatorProps> = ({ onSend }) => {
  const [question, setQuestion] = useState("");
  const [questionError, setQuestionError] = useState("");
  const [options, setOptions] = useState<string[]>(["", ""]);
  const [optionErrors, setOptionErrors] = useState<string[]>(["", ""]);
  const [allowMultiple, setAllowMultiple] = useState(true);
  const [loading, setLoading] = useState(false);

  // ── Validation helpers ────────────────────────────────────────────────────

  const validateQuestion = (value: string): string => {
    if (!value.trim()) return "Question cannot be empty";
    return "";
  };

  const validateOptions = (opts: string[]): string[] => {
    const errors: string[] = opts.map(() => "");
    const seen = new Map<string, number>(); // lowercase → first index

    opts.forEach((opt, i) => {
      if (!opt.trim()) {
        errors[i] = "Option cannot be empty";
        return;
      }
      const lower = opt.trim().toLowerCase();
      if (seen.has(lower)) {
        errors[i] = "This is already an option";
      } else {
        seen.set(lower, i);
      }
    });

    return errors;
  };

  // ── Field change handlers ────────────────────────────────────────────────

  const handleQuestionChange = (value: string) => {
    setQuestion(value);
    if (questionError) setQuestionError("");
  };

  const handleQuestionBlur = () => {
    setQuestionError(validateQuestion(question));
  };

  const handleOptionChange = (index: number, value: string) => {
    const newOptions = [...options];
    newOptions[index] = value;
    setOptions(newOptions);
    // Clear only this field's error while typing; full re-validate on blur
    if (optionErrors[index]) {
      const cleared = [...optionErrors];
      cleared[index] = "";
      setOptionErrors(cleared);
    }
  };

  const handleOptionBlur = (index: number) => {
    setOptionErrors(validateOptions(options));
  };

  // ── Option list management ───────────────────────────────────────────────

  const addOption = () => {
    if (options.length >= MAX_OPTIONS) return;
    setOptions([...options, ""]);
    setOptionErrors([...optionErrors, ""]);
  };

  const removeOption = (index: number) => {
    if (options.length <= MIN_OPTIONS) return;
    const newOptions = options.filter((_, i) => i !== index);
    setOptions(newOptions);
    setOptionErrors(validateOptions(newOptions));
  };

  const handleDragEnd = (result: any) => {
    if (!result.destination) return;
    const reordered = Array.from(options);
    const [moved] = reordered.splice(result.source.index, 1);
    reordered.splice(result.destination.index, 0, moved);
    setOptions(reordered);
    setOptionErrors(validateOptions(reordered));
  };

  // ── Submit ───────────────────────────────────────────────────────────────

  const handleSubmit = async () => {
    const qError = validateQuestion(question);
    const oErrors = validateOptions(options);

    setQuestionError(qError);
    setOptionErrors(oErrors);

    if (qError || oErrors.some((e) => e)) return;

    setLoading(true);
    await onSend(question.trim(), options.map((o) => o.trim()), allowMultiple);
    setQuestion("");
    setOptions(["", ""]);
    setOptionErrors(["", ""]);
    setQuestionError("");
    setLoading(false);
  };

  return (
    <div className="poll-creator">
      <Title level={4}>Create poll</Title>

      {/* Question */}
      <div className="poll-section">
        <label>Question</label>
        <Input
          placeholder="Ask question"
          value={question}
          onChange={(e) => handleQuestionChange(e.target.value)}
          onBlur={handleQuestionBlur}
          status={questionError ? "error" : ""}
        />
        {questionError && (
          <span className="poll-field-error">{questionError}</span>
        )}
      </div>

      {/* Options */}
      <div className="poll-section">
        <label>Options</label>
        <DragDropContext onDragEnd={handleDragEnd}>
          <Droppable droppableId="options">
            {(provided: any) => (
              <div {...provided.droppableProps} ref={provided.innerRef}>
                {options.map((opt, index) => (
                  <Draggable
                    key={index.toString()}
                    draggableId={index.toString()}
                    index={index}
                  >
                    {(provided: any) => (
                      <div
                        className="option-item-wrapper"
                        ref={provided.innerRef}
                        {...provided.draggableProps}
                      >
                        <div className="option-item">
                          <span
                            className="drag-handle"
                            {...provided.dragHandleProps}
                          >
                            &#9776;
                          </span>
                          <Input
                            placeholder={`Option ${index + 1}`}
                            value={opt}
                            onChange={(e) =>
                              handleOptionChange(index, e.target.value)
                            }
                            onBlur={() => handleOptionBlur(index)}
                            status={optionErrors[index] ? "error" : ""}
                          />
                          <Button
                            type="text"
                            danger
                            icon={<DeleteOutlined />}
                            onClick={() => removeOption(index)}
                            disabled={options.length <= MIN_OPTIONS}
                            className="remove-option-btn"
                          />
                        </div>
                        {optionErrors[index] && (
                          <span className="poll-field-error">
                            {optionErrors[index]}
                          </span>
                        )}
                      </div>
                    )}
                  </Draggable>
                ))}
                {provided.placeholder}
              </div>
            )}
          </Droppable>
        </DragDropContext>

        {options.length < MAX_OPTIONS && (
          <Button type="link" onClick={addOption}>
            + Add option
          </Button>
        )}
      </div>

      {/* Allow multiple */}
      <div className="poll-toggle">
        <span>Allow multiple answers</span>
        <Switch checked={allowMultiple} onChange={setAllowMultiple} defaultChecked />
      </div>

      {/* Submit */}
      <div className="poll-submit">
        <GradientButton
          disabled={loading}
          type="primary"
          shape="circle"
          icon={<SendOutlined />}
          onClick={handleSubmit}
        />
      </div>
    </div>
  );
};

export default PollCreator;
